import {
  PLAUSIBLE_TEMP_DISABLED,
  isPlausibleAllowedOnCurrentHost,
  isPlausibleTestMode,
} from '../config/plausible';
import { getMergedUtmParams } from './utm';
import { getCuberaAttribution } from './cuberaPixel';

const PLAUSIBLE_EVENT_API = 'https://plausible.io/api/event';
const PLAUSIBLE_SITE_DOMAIN = 'daivbharathi.com';

/**
 * UTM + campaign fields for Plausible custom props (campaign-wise filters).
 * Values come from current URL (preferred) + stored UTM + Cubera attribution.
 * Prop values must be scalars (Plausible rejects objects/arrays).
 */
export function getPlausibleAttributionProps() {
  if (typeof window === 'undefined') return {};

  const search = window.location?.search || '';
  const utm = getMergedUtmParams(search);
  const cubera = getCuberaAttribution();
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);

  const utm_source = String(utm.utm_source || params.get('utm_source') || '').trim();
  const utm_medium = String(utm.utm_medium || params.get('utm_medium') || '').trim();
  const utm_campaign = String(
    utm.utm_campaign || params.get('utm_campaign') || cubera.campaign || '',
  ).trim();
  const utm_content = String(params.get('utm_content') || '').trim();
  const utm_term = String(params.get('utm_term') || '').trim();
  const campaign = String(cubera.campaign || utm_campaign || params.get('campaign') || '').trim();
  // Cubera ads use utm_click (not click_id). Do not treat fbclid/gclid as Cubera.
  const cuberaClick = String(
    cubera.click_id || params.get('click_id') || params.get('utm_click') || '',
  ).trim();
  const click_id = String(
    cuberaClick || params.get('utm_id') || params.get('gclid') || params.get('fbclid') || '',
  ).trim();
  const campaign_id = String(
    params.get('campaign_id') || params.get('utm_id') || campaign || utm_campaign || '',
  ).trim();
  // Only attach real utm_source — never invent "cubera".
  const resolvedSource = utm_source;

  const props = {};
  if (resolvedSource) props.utm_source = resolvedSource;
  if (utm_medium) props.utm_medium = utm_medium;
  if (utm_campaign) props.utm_campaign = utm_campaign;
  if (utm_content) props.utm_content = utm_content;
  if (utm_term) props.utm_term = utm_term;
  if (campaign) props.campaign = campaign;
  if (campaign_id) props.campaign_id = campaign_id;
  if (click_id) props.click_id = click_id;
  return props;
}

function withAttributionProps(options) {
  const attribution = getPlausibleAttributionProps();
  if (!Object.keys(attribution).length && !options) return undefined;

  const next = { ...(options || {}) };
  next.props = {
    ...attribution,
    ...(options?.props && typeof options.props === 'object' ? options.props : {}),
  };
  if (!Object.keys(next.props).length) delete next.props;
  return Object.keys(next).length ? next : undefined;
}

function getPlausibleDomain() {
  const host = String(window.location?.hostname || '').toLowerCase();
  if (host === 'daivbharathi.com' || host === 'www.daivbharathi.com') return PLAUSIBLE_SITE_DOMAIN;
  // Local / preview tests with REACT_APP_PLAUSIBLE_ENABLED=true still land on prod site goals.
  if (isPlausibleTestMode()) return PLAUSIBLE_SITE_DOMAIN;
  return host || PLAUSIBLE_SITE_DOMAIN;
}

/** Beacon/fetch fallback when the Plausible script is blocked or not ready. */
function sendPlausibleEventViaApi(name, options) {
  if (PLAUSIBLE_TEMP_DISABLED || !isPlausibleAllowedOnCurrentHost()) return;
  if (typeof window === 'undefined' || !name) return;
  const merged = withAttributionProps(options) || {};
  const props = {};
  if (merged.props && typeof merged.props === 'object') {
    Object.entries(merged.props).forEach(([key, value]) => {
      if (value == null || value === '') return;
      props[key] = String(value);
    });
  }
  const body = {
    n: name,
    u: String(window.location.href || `https://${PLAUSIBLE_SITE_DOMAIN}/`),
    d: getPlausibleDomain(),
  };
  if (typeof document !== 'undefined' && document.referrer) body.r = document.referrer;
  if (Object.keys(props).length) body.p = props;
  if (merged.revenue && Number.isFinite(Number(merged.revenue.amount))) {
    body.$ = {
      amount: Number(merged.revenue.amount),
      currency: String(merged.revenue.currency || 'INR'),
    };
  }

  const json = JSON.stringify(body);
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([json], { type: 'text/plain' });
      if (navigator.sendBeacon(PLAUSIBLE_EVENT_API, blob)) return;
    }
  } catch {
    /* fall through */
  }
  try {
    fetch(PLAUSIBLE_EVENT_API, {
      method: 'POST',
      body: json,
      headers: { 'Content-Type': 'text/plain' },
      keepalive: true,
      mode: 'no-cors',
      credentials: 'omit',
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}

export function trackPlausiblePageView() {
  if (!isPlausibleAllowedOnCurrentHost()) return;

  if (typeof window.plausible === 'function') {
    window.plausible('pageview', withAttributionProps());
    return;
  }
  sendPlausibleEventViaApi('pageview');
}

/**
 * Track custom event. Prefers loaded script; falls back to Events API so UTM/Purchase
 * still capture when the script is slow or blocked.
 */
export function trackPlausibleEvent(name, options) {
  if (!isPlausibleAllowedOnCurrentHost()) return;
  if (!name) return;

  if (typeof window.plausible === 'function') {
    window.plausible(name, withAttributionProps(options));
    return;
  }
  sendPlausibleEventViaApi(name, options);
}
